import { InvoiceTemplateInterface } from './invoiceTemplates';

/**
 * Generates a complete HTML string with inline CSS for a custom invoice template.
 * @param templateConfig The configuration for the custom template
 * @param sampleData Optional sample data for preview
 * @returns HTML string
 */
export const generateCustomTemplateHTML = (
    templateConfig: InvoiceTemplateInterface,
    sampleData?: any
): string => {
    const { colorScheme, fontFamily, layout } = templateConfig;
    const primary = colorScheme?.primary || '#333';
    const secondary = colorScheme?.secondary || '#666';
    const background = colorScheme?.background || '#fff';
    const text = colorScheme?.text || '#000';

    // Sample data fallback
    const data = sampleData || {
        businessName: 'Your Business Name',
        businessAddress: '123 Business St, City, Country',
        clientName: 'Client Name',
        clientAddress: '456 Client Rd, City, Country',
        invoiceNumber: 'INV-001',
        date: '2023-10-25',
        items: [
            { name: 'Item 1', quantity: 1, rate: 100, amount: 100 },
            { name: 'Item 2', quantity: 2, rate: 50, amount: 100 }
        ],
        subtotal: 200,
        tax: 20,
        total: 220
    };

    // Determine section order based on layout (simplistic implementation for now)
    // In a real DnD builder, we would have an ordered array of section IDs in the templateConfig
    // prioritizing the passed config layout if it were more complex.

    // Structure:
    // 1. Header (Logo, Title, Business Info)
    // 2. Info Bar (Inv #, Date, Bill To)
    // 3. Items Table
    // 4. Totals
    // 5. Footer (Notes, Terms)

    const fontStyle = `font-family: ${fontFamily || 'Arial, sans-serif'};`;

    return `
    <!DOCTYPE html>
    <html>
    <head>
        <style>
            body {
                margin: 0;
                padding: 20px;
                background-color: ${background};
                color: ${text};
                ${fontStyle}
                font-size: 14px;
            }
            .container {
                max-width: 800px;
                margin: 0 auto;
                background: white;
                min-height: 1000px;
            }
            .header {
                display: flex;
                justify-content: space-between;
                padding: 20px 0;
                border-bottom: 2px solid ${primary};
                margin-bottom: 20px;
            }
            .business-title {
                font-size: 24px;
                color: ${primary};
                font-weight: bold;
                margin-bottom: 5px;
            }
            .info-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 20px;
                margin-bottom: 30px;
            }
            .label {
                font-size: 12px;
                color: ${secondary};
                font-weight: bold;
                text-transform: uppercase;
            }
            .table-container {
                margin-bottom: 30px;
            }
            table {
                width: 100%;
                border-collapse: collapse;
            }
            th {
                background-color: ${primary};
                color: white;
                padding: 10px;
                text-align: left;
            }
            td {
                padding: 10px;
                border-bottom: 1px solid #eee;
            }
            .totals {
                display: flex;
                justify-content: flex-end;
                margin-bottom: 30px;
            }
            .totals-table {
                width: 300px;
            }
            .row {
                display: flex;
                justify-content: space-between;
                padding: 5px 0;
            }
            .total-row {
                font-weight: bold;
                font-size: 16px;
                color: ${primary};
                border-top: 1px solid ${primary};
                padding-top: 10px;
            }
            .footer {
                margin-top: 50px;
                text-align: center;
                font-size: 12px;
                color: ${secondary};
                border-top: 1px solid #eee;
                padding-top: 20px;
            }
        </style>
    </head>
    <body>
        <div class="container">
            <!-- Header Section -->
            <div class="header">
                <div>
                    <div class="business-title">${data.businessName}</div>
                    <div>${data.businessAddress}</div>
                </div>
                <div style="text-align: right;">
                    <div style="font-size: 30px; color: ${secondary};">INVOICE</div>
                    <div>#${data.invoiceNumber}</div>
                </div>
            </div>

            <!-- Client & Meta Info -->
            <div class="info-grid">
                <div>
                    <div class="label">Bill To</div>
                    <div style="font-weight: bold; margin-top: 5px;">${data.clientName}</div>
                    <div>${data.clientAddress}</div>
                </div>
                <div style="text-align: right;">
                    <div class="label">Date</div>
                    <div style="margin-top: 5px;">${data.date}</div>
                </div>
            </div>

            <!-- Items Table -->
            <div class="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Description</th>
                            <th style="width: 80px; text-align: center;">Qty</th>
                            <th style="width: 100px; text-align: right;">Rate</th>
                            <th style="width: 100px; text-align: right;">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${data.items.map((item: any) => `
                        <tr>
                            <td>${item.name}</td>
                            <td style="text-align: center;">${item.quantity}</td>
                            <td style="text-align: right;">${item.rate}</td>
                            <td style="text-align: right;">${item.amount}</td>
                        </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>

            <!-- Totals -->
            <div class="totals">
                <div class="totals-table">
                    <div class="row">
                        <span>Subtotal</span>
                        <span>${data.subtotal}</span>
                    </div>
                    <div class="row">
                        <span>Tax</span>
                        <span>${data.tax}</span>
                    </div>
                    <div class="row total-row">
                        <span>Total</span>
                        <span>${data.total}</span>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="footer">
                <div>Thank you for your business!</div>
            </div>
        </div>
    </body>
    </html>
    `;
};
